import {
  ColorListSparkLineSetting as SparkLine,
  CMS_CONTRACT_AFFILIATIONS,
  COLOR_LIST_MAX_CONTRACTS,
  COLOR_LIST_STATUS_TEXT_COLORS,
} from '../cms-config.js';
import { priceCents, safeColor, firstText } from '../cms-market-helpers.js';

const array = (value) => (Array.isArray(value) ? value : []);

export function resolveColorListSettings(list, uuid, index) {
  return (
    array(list).find(
      (item) => item && (uuid ? item.regionUUID === uuid : true) && item.colorListIndex === index,
    ) || null
  );
}

export function historicalSparkline(rawPoints) {
  const days = new Map();
  for (const point of array(rawPoints)) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(point?.date || '') || priceCents(point.price) === null)
      continue;
    const time = Date.parse(`${point.date}T00:00:00Z`);
    if (Number.isFinite(time)) days.set(point.date, { time, price: Number(point.price) });
  }
  const points = [...days.values()].sort((a, b) => a.time - b.time);
  if (points.length < 2) return null;
  const first = points[0],
    last = points.at(-1);
  const path = points
    .map((p, i) => {
      const x = 2 + ((p.time - first.time) / (last.time - first.time)) * 60;
      const y = 26 - p.price * 22;
      return `${i ? 'L' : 'M'}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');
  return { points, path, trend: Math.sign(last.price - first.price) };
}

function raceStatus(rows) {
  const leaders = rows.filter((row) => row.isLeader);
  if (!leaders.length) return null;
  const leader = leaders[0];
  if (leader.price <= 55)
    return { label: 'Toss-up', textColors: COLOR_LIST_STATUS_TEXT_COLORS.tossUp };
  if (new Set(leaders.map((row) => row.affiliation)).size > 1) return null;
  // No party label can resolve a tie between different unaffiliated candidates.
  if (!leader.affiliation && new Set(leaders.map((row) => row.name)).size > 1) return null;
  const level = leader.price <= 65 ? 0 : leader.price <= 75 ? 1 : 2;
  return {
    label: `${['Slight Lean', 'Lean', 'Likely'][level]} ${CMS_CONTRACT_AFFILIATIONS[leader.affiliation]?.label || leader.name}`,
    textColors: COLOR_LIST_STATUS_TEXT_COLORS[leader.affiliation]?.[level] || [
      leader.color,
      leader.color,
    ],
  };
}

export function buildColorList(settings, market, data, showOdds) {
  if (!settings || !market) return null;
  const symbol = showOdds ? '%' : '¢';
  const custom = settings.sparkLineSetting === SparkLine.Custom;
  const numDays = Math.min(
    30,
    Math.max(1, Number(data?.numDays || settings.sparkLineNumDays || 30)),
  );
  const rows = array(settings.contracts).flatMap((meta) => {
    const contract = array(market.contracts).find((c) => c.contractId === Number(meta.id));
    if (!contract) return [];
    const history = array(data?.contracts).find((c) => c.contractId === contract.contractId);
    const party = CMS_CONTRACT_AFFILIATIONS[meta.affiliation];
    const price = priceCents(contract.lastTradePrice);
    const baseline = priceCents(custom ? history?.baselinePrice : contract.lastClosePrice);
    const quote = priceCents(contract.bestYesPrice);
    return [
      {
        id: contract.contractId,
        contract,
        name: firstText(meta.name, contract.contractShortName, contract.contractName),
        subtitle: [
          firstText(meta.subtitle, party?.name),
          meta.isIncumbent === true ? 'Incumbent' : '',
        ]
          .filter(Boolean)
          .join(' · '),
        image: firstText(meta.imagePath, contract.contractImageUrl),
        overlay: firstText(meta.imageOverlayText),
        color: safeColor(meta.colorHex, party?.color || '#6B7280'),
        affiliation: party ? Number(meta.affiliation) : 0,
        isIncumbent: meta.isIncumbent === true,
        price,
        display: price === null ? '—' : `${price}${symbol}`,
        buyPrice: quote > 0 ? quote : null,
        change: price === null || baseline === null ? null : price - baseline,
        sparkline:
          settings.sparkLineSetting === SparkLine.None
            ? null
            : historicalSparkline(history?.points),
      },
    ];
  });
  const prices = rows.filter((row) => row.price !== null).map((row) => row.price);
  const max = prices.length ? Math.max(...prices) : null;
  rows.forEach((row) => {
    row.isLeader = row.price !== null && row.price === max;
  });
  const visibleRows = rows.slice(0, COLOR_LIST_MAX_CONTRACTS);
  const totalContractCount =
    Number.isSafeInteger(data?.totalContractCount) && data.totalContractCount >= rows.length
      ? data.totalContractCount
      : rows.length;
  return {
    title: firstText(settings.market?.name, market.marketName),
    shortName: firstText(settings.market?.marketPsuedoShortname),
    date: firstText(settings.market?.eventDate),
    summaryText: firstText(settings.market?.contestantSummaryText),
    symbol,
    numDays,
    changeLabel: custom ? `${numDays}d` : '',
    rows: visibleRows,
    contractCount:
      totalContractCount > visibleRows.length
        ? `${visibleRows.length}/${totalContractCount} contracts`
        : '',
    raceStatus: raceStatus(rows),
    spread: rows.length === 2 && prices.length === 2 ? Math.abs(prices[0] - prices[1]) : null,
  };
}
